import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import { IMedia } from "src/model/unityObject";

interface StagedReferenceImageCardProps {
  media: IMedia;
  /** True while the integrate request (a decor-realign refine turn) is in flight. */
  busy: boolean;
  onIntegrate: () => void;
  onCancel: () => void;
}

// The "loud staging" card: a picked-but-not-yet-integrated reference image.
// Full-width, thumbnailed, with a real CTA — deliberately impossible to
// mistake for a done state (the invisible staged-chip failure this replaces).
// Rendered above the composer at the Plan stage and inside the Options
// Blockade section, so the pending image is visible wherever the user is.
const StagedReferenceImageCard = ({
  media,
  busy,
  onIntegrate,
  onCancel,
}: StagedReferenceImageCardProps) => {
  const { t } = useTranslation();
  const name = media.filename || media.name;

  return (
    <div className="w-full flex items-center gap-2.5 rounded-md border bg-purple-50 border-purple-200 px-3 py-2 mb-1.5">
      <img
        src={media.thumbnail_s3_url || media.s3_url}
        alt={name}
        className="h-10 w-10 rounded object-cover border border-purple-200 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-purple-800 truncate">{name}</p>
        <p className="text-xs text-purple-600">{t("pages.chat.stagedReferenceImageStatus")}</p>
      </div>
      <button
        type="button"
        className="btn-primary-fill text-sm shrink-0 disabled:opacity-60"
        disabled={busy}
        onClick={onIntegrate}
      >
        {busy
          ? t("pages.chat.stagedReferenceImageBusy")
          : t("pages.chat.stagedReferenceImageIntegrate")}
      </button>
      <button
        type="button"
        aria-label={t("pages.chat.stagedReferenceImageCancel")}
        disabled={busy}
        className="text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        onClick={onCancel}
      >
        <MdClose size={16} />
      </button>
    </div>
  );
};

export default StagedReferenceImageCard;
